import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap"

import getColValue from "./regionCol"

import Breadcrumbs from "components/Common/Breadcrumb"
import TableContainerV1 from "components/Common/TableContainerV1"
import { deleteRegion, getRegionList } from "http/Region"
import ToastDialog from "components/Common/ToastDialog"

import DeleteModal from "components/Common/DeleteModal"

const TableActionsComponent = (props, handleDeleteRegion = null) => {
  const navigate = useNavigate()
  const { roleName } = JSON.parse(localStorage.getItem("user"))

  const navigateToEditRegion = id => {
    const encryptedId = btoa(id.toString())
    navigate(`/${roleName}/editRegion?id=${encodeURIComponent(encryptedId)}`)
  }

  return (
    <UncontrolledDropdown>
      <DropdownToggle tag="a" className="card-drop">
        <i className="mdi mdi-dots-horizontal font-size-18"></i>
      </DropdownToggle>

      <DropdownMenu className="dropdown-menu-end">
        <DropdownItem
          onClick={() => {
            navigateToEditRegion(props.id)
          }}
        >
          <i
            className="mdi mdi-pencil font-size-16 text-success me-1"
            id="edittooltip"
          ></i>
          Edit
          <UncontrolledTooltip placement="top" target="edittooltip">
            Edit
          </UncontrolledTooltip>
        </DropdownItem>

        <DropdownItem onClick={() => handleDeleteRegion(props.id)}>
          <i
            className="mdi mdi-trash-can font-size-16 text-danger me-1"
            id="deletetooltip"
          ></i>
          Delete
          <UncontrolledTooltip placement="top" target="deletetooltip">
            Delete
          </UncontrolledTooltip>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

const RegionList = () => {
  document.title = "Region List | DMS"
  const { roleName } = JSON.parse(localStorage.getItem("user"))

  const navigate = useNavigate()
  const [regionList, setRegionList] = useState([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  })
  const [pageSize, setPageSize] = useState(10)
  const [searchValue, setSearchValue] = useState("")
  const [columnId, setColumnId] = useState("id")
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc")
  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action"],
  })
  const [toastInfo, setToastInfo] = useState({ showToast: false })
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteItemId, setDeleteItemId] = useState(null)

  const columns = useMemo(
    () => [
      {
        Header: "Region ID",
        accessor: "id",
        filterable: true,
        Cell: cellProps => {
          return getColValue(cellProps)
        },
      },
      {
        Header: "Region Name",
        accessor: "regionName",
        filterable: true,
        Cell: cellProps => {
          return getColValue(cellProps)
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          const rowData = cellProps.row.original
          return TableActionsComponent(rowData, handleDeleteRegion)
        },
      },
    ],
    []
  )

  const handleDeleteRegion = id => {
    setDeleteItemId(id)
    setDeleteModal(true)
  }
  const deleteRegionHelper = () => {
    deleteRegion({ regionId: deleteItemId })
      .then(responseFromAPI => {
        if (responseFromAPI.status) {
          setToastInfo({
            message: responseFromAPI.message,
            showToast: true,
            role: "success",
          })
          setDeleteModal(false)
          getRegionListHelper({})
        } else {
          setToastInfo({
            message: responseFromAPI.message,
            showToast: true,
            role: "error",
          })
          setDeleteModal(false)
        }
      })
      .catch(error => { })
  }

  const getRegionListHelper = data => {
    getRegionList(data)
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          setRegionList(responseFromAPI.data.data)
          const { totalItems, totalPages, currentPage } = responseFromAPI.data
          setPagination({
            pageIndex: currentPage,
            pageCount: totalPages,
            totalRecords: totalItems,
          })
        } else {
          setPagination({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
          setRegionList([])
        }
      })
      .catch(error => { })
  }

  const handlePageChange = (
    page,
    pageSize,
    searchKey=searchValue,
    columnName = columnId,
    sortingOrder = sortingOrderStatus
  ) => {
    setPageSize(pageSize)
    getRegionListHelper({
      page,
      limit: pageSize,
      searchKey,
      columnName,
      sortingOrder,
    })
  }

  const handleSortColumn = option => {
    setSortingOptions(option)
    const columnName = option.column
    setColumnId(columnName)
    const sortingOrder = option.isAsc ? "asc" : "desc"
    setSortingOrderStatus(sortingOrder)
    handlePageChange(1, pageSize, searchValue, columnName, sortingOrder)
  }

  const handleSearchFilter = (value, pageSize) => {
    setSearchValue(value)
    handlePageChange(1, pageSize, value)
  }
  const handleClearSearch = () => {
    setSearchValue("")
    handlePageChange(1, pageSize, "")
  }

  useEffect(() => {
    getRegionListHelper({})
  }, [])

  const handleCustomerClicks = () => {
    navigate(`/${roleName}/addRegion`)
  }

  const buttonSection = (
    <div className="text-sm-end">
      <Button
        type="button"
        color="success"
        className="btn-rounded mb-2 me-2"
        onClick={handleCustomerClicks}
      >
        <i className="mdi mdi-plus me-1" />
        {"Add Region"}
      </Button>
    </div>
  )

  return (
    <div className="page-content">
      <div className="ms-2">
        <Breadcrumbs title="Region" breadcrumbItem="Region List" />
      </div>
      <Container fluid>
        <Row className="vertical-scroll">
          <Col xs="12">
            <Card>
              <CardBody>
                <TableContainerV1
                  columns={columns}
                  data={regionList}
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  handleSearchFilter={handleSearchFilter}
                  customSection={buttonSection}
                  sortOptions={sortingOptions}
                  searchHelperText="Search by Region Name"
                  handleSortColumn={handleSortColumn}
                  handleClearSearch={handleClearSearch}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteRegionHelper}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ToastDialog
        message={toastInfo.message || ""}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      ></ToastDialog>
    </div>
  )
}

export default RegionList
